import { useEffect, useState, type FormEvent } from 'react'
import { createTeam, getMyTeams, getTeam, inviteToTeam, type Team, type TeamDetail } from '../../lib/api'

export function TeamsSection() {
  const [teams, setTeams] = useState<Team[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [newTeamName, setNewTeamName] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [expandedTeamId, setExpandedTeamId] = useState<string | null>(null)

  function loadTeams() {
    return getMyTeams()
      .then(setTeams)
      .catch(() => {
        // Best-effort — the section still renders an empty state.
      })
  }

  useEffect(() => {
    let cancelled = false
    loadTeams().finally(() => {
      if (!cancelled) setIsLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [])

  async function handleCreate(e: FormEvent) {
    e.preventDefault()
    if (!newTeamName.trim() || isCreating) return
    setIsCreating(true)
    setCreateError(null)
    try {
      await createTeam(newTeamName.trim())
      setNewTeamName('')
      await loadTeams()
    } catch {
      setCreateError('No se pudo crear el equipo. Intenta de nuevo.')
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <section>
      <p className="account-section__eyebrow">SateLab</p>
      <h1 className="account-section__title">Equipos</h1>
      <p className="account-section__subtitle">Equipos que administras o de los que eres miembro en el ecosistema.</p>

      <div className="account-section__body">
        {isLoading && <p className="account-status">Cargando equipos…</p>}

        {!isLoading && teams.length === 0 && <div className="account-empty">Aún no perteneces a ningún equipo.</div>}

        {!isLoading && teams.length > 0 && (
          <div className="account-list">
            {teams.map((team) => (
              <TeamCard
                key={team.id}
                team={team}
                isExpanded={expandedTeamId === team.id}
                onToggle={() => setExpandedTeamId((current) => (current === team.id ? null : team.id))}
              />
            ))}
          </div>
        )}

        <form className="account-inline-form" onSubmit={handleCreate}>
          <label className="sr-only" htmlFor="new-team-name">
            Nombre del nuevo equipo
          </label>
          <input
            id="new-team-name"
            className="auth-input"
            placeholder="Nombre del nuevo equipo"
            value={newTeamName}
            onChange={(e) => setNewTeamName(e.target.value)}
          />
          <button type="submit" className="auth-button" disabled={isCreating}>
            {isCreating ? 'Creando…' : 'Crear equipo'}
          </button>
        </form>
        {createError && (
          <p className="auth-error" role="alert">
            {createError}
          </p>
        )}
      </div>
    </section>
  )
}

interface TeamCardProps {
  team: Team
  isExpanded: boolean
  onToggle: () => void
}

function TeamCard({ team, isExpanded, onToggle }: TeamCardProps) {
  const [detail, setDetail] = useState<TeamDetail | null>(null)
  const [inviteEmail, setInviteEmail] = useState('')
  const [isInviting, setIsInviting] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [inviteSent, setInviteSent] = useState(false)

  useEffect(() => {
    if (!isExpanded) return
    let cancelled = false
    getTeam(team.id).then((result) => {
      if (!cancelled) setDetail(result)
    })
    return () => {
      cancelled = true
    }
  }, [isExpanded, team.id])

  async function handleInvite(e: FormEvent) {
    e.preventDefault()
    if (!inviteEmail.trim() || isInviting) return
    setIsInviting(true)
    setInviteError(null)
    setInviteSent(false)
    try {
      await inviteToTeam(team.id, inviteEmail.trim())
      setInviteEmail('')
      setInviteSent(true)
    } catch {
      setInviteError('No se pudo enviar la invitación. Solo el propietario del equipo puede invitar.')
    } finally {
      setIsInviting(false)
    }
  }

  return (
    <div className="account-card">
      <p className="account-card__title">{team.name}</p>
      <p className="account-card__meta">{team.myRole === 'owner' ? 'Propietario' : 'Miembro'}</p>

      <div className="account-card__actions">
        <button type="button" className="account-link-button" onClick={onToggle}>
          {isExpanded ? 'Ocultar detalle' : 'Ver detalle'}
        </button>
      </div>

      {isExpanded && (
        <div className="account-section__body">
          {!detail && <p className="account-status">Cargando miembros…</p>}
          {detail && (
            <>
              <div className="account-card__entitlements">
                {detail.members.map((member) => (
                  <span key={member.id} className="account-badge">
                    {member.user.name} ({member.role})
                  </span>
                ))}
              </div>

              {team.myRole === 'owner' && (
                <form className="account-inline-form" onSubmit={handleInvite}>
                  <label className="sr-only" htmlFor={`invite-email-${team.id}`}>
                    Email a invitar
                  </label>
                  <input
                    id={`invite-email-${team.id}`}
                    type="email"
                    className="auth-input"
                    placeholder="Email a invitar"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                  />
                  <button type="submit" className="auth-button" disabled={isInviting}>
                    {isInviting ? 'Invitando…' : 'Invitar'}
                  </button>
                </form>
              )}
              {inviteSent && (
                <p className="account-success" role="status">
                  Invitación enviada.
                </p>
              )}
              {inviteError && (
                <p className="auth-error" role="alert">
                  {inviteError}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
